import jsPDF from "jspdf";

const PAGE_MARGIN = 40;
const LINE_GAP = 6;
const SECTION_GAP = 14;

const slug = (s) =>
  String(s || "candidate-comparison")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 60) || "candidate-comparison";

const fmt = (v, digits = 1) =>
  v == null || (typeof v === "number" && Number.isNaN(v)) ? "—" : Number(v).toFixed(digits);

const ensureSpace = (doc, y, needed = 0) => {
  if (y + needed > doc.internal.pageSize.getHeight() - PAGE_MARGIN) {
    doc.addPage();
    return PAGE_MARGIN;
  }
  return y;
};

const writeWrapped = (doc, text, x, y, maxWidth, lineHeight) => {
  const lines = doc.splitTextToSize(String(text ?? ""), maxWidth);
  for (const line of lines) {
    y = ensureSpace(doc, y, lineHeight);
    doc.text(line, x, y);
    y += lineHeight;
  }
  return y;
};

const writeSectionHeading = (doc, heading, x, y) => {
  y = ensureSpace(doc, y, 22);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(11);
  doc.setTextColor(71, 85, 105);
  doc.text(String(heading || "").toUpperCase(), x, y);
  doc.setTextColor(15, 23, 42);
  return y + LINE_GAP + 6;
};

// Tinted band cell for the heatmap. RGB returned by jsPDF doc.setFillColor(rgb).
// Score scales:
//   "skill"    → 0–100  (90+ emerald, 75 emerald-light, 60 amber, 40 orange, else red)
//   "question" → 0–10   (8+ emerald, 6 emerald-light, 4 amber, 2 orange, else red)
const bandColor = (score, scale = "skill") => {
  if (score == null) return [248, 250, 252];
  const s = Number(score);
  const thresholds = scale === "question" ? [8, 6, 4, 2] : [90, 75, 60, 40];
  if (s >= thresholds[0]) return [16, 185, 129];
  if (s >= thresholds[1]) return [110, 231, 183];
  if (s >= thresholds[2]) return [251, 191, 36];
  if (s >= thresholds[3]) return [249, 115, 22];
  return [239, 68, 68];
};

const drawCell = (doc, x, y, w, h, text, fill, fg = [255, 255, 255]) => {
  doc.setFillColor(fill[0], fill[1], fill[2]);
  doc.rect(x, y, w, h, "F");
  doc.setTextColor(fg[0], fg[1], fg[2]);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(10);
  doc.text(String(text), x + w / 2, y + h / 2 + 3, { align: "center" });
  doc.setTextColor(15, 23, 42);
  doc.setFont("helvetica", "normal");
};

// Render a candidates × rows heatmap. `rows` is [{label, secondary?, scoreByUser}].
// scoreByUser is a map keyed by candidate.userId or a function (candidate) => score.
const drawHeatmap = (doc, candidates, rows, y, opts = {}) => {
  const { scale = "skill", labelCol = 200, secondaryCol = 0 } = opts;
  const pageWidth = doc.internal.pageSize.getWidth();
  const colWidth = Math.min(
    72,
    Math.max(48, (pageWidth - PAGE_MARGIN * 2 - labelCol - secondaryCol) / Math.max(candidates.length, 1))
  );
  const cellH = 22;
  const headerH = 24;

  // Header
  y = ensureSpace(doc, y, headerH + 4);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(10);
  doc.setFillColor(241, 245, 249);
  doc.rect(PAGE_MARGIN, y, labelCol + secondaryCol + colWidth * candidates.length, headerH, "F");
  doc.setTextColor(71, 85, 105);
  doc.text("Skill", PAGE_MARGIN + 6, y + headerH / 2 + 3);
  if (secondaryCol > 0) doc.text("Competency", PAGE_MARGIN + labelCol + 6, y + headerH / 2 + 3);
  candidates.forEach((c, i) => {
    const cx = PAGE_MARGIN + labelCol + secondaryCol + colWidth * i + colWidth / 2;
    const name = doc.splitTextToSize(c.fullName || `User ${c.userId}`, colWidth - 4)[0] || "";
    doc.text(name, cx, y + headerH / 2 + 3, { align: "center" });
  });
  doc.setTextColor(15, 23, 42);
  y += headerH;

  // Rows
  for (const row of rows) {
    y = ensureSpace(doc, y, cellH + 4);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(9);
    const labelLines = doc.splitTextToSize(row.label || "", labelCol - 8);
    doc.text(labelLines[0] || "", PAGE_MARGIN + 4, y + cellH / 2 + 3);
    if (secondaryCol > 0) {
      doc.setTextColor(100, 116, 139);
      doc.text(String(row.secondary || "—").slice(0, 20), PAGE_MARGIN + labelCol + 4, y + cellH / 2 + 3);
      doc.setTextColor(15, 23, 42);
    }
    const rowY = y;
    for (let i = 0; i < candidates.length; i++) {
      const c = candidates[i];
      const cx = PAGE_MARGIN + labelCol + secondaryCol + colWidth * i;
      const score = typeof row.scoreByUser === "function" ? row.scoreByUser(c) : row.scoreByUser?.[c.userId];
      const text = score == null ? "—" : Number(score).toFixed(scale === "question" ? 1 : 0);
      const fill = bandColor(score, scale);
      const fg = score == null
        ? [148, 163, 184]
        : (Number(score) >= (scale === "question" ? 6 : 75) && Number(score) < (scale === "question" ? 8 : 90))
        ? [6, 78, 59]
        : [255, 255, 255];
      drawCell(doc, cx + 1, rowY + 1, colWidth - 2, cellH - 2, text, fill, fg);
    }
    y += cellH;
  }

  return y;
};

export const compareToPdfBlob = (data) => {
  const doc = new jsPDF({ unit: "pt", format: "a4", orientation: "landscape" });
  const pageWidth = doc.internal.pageSize.getWidth();
  const contentWidth = pageWidth - PAGE_MARGIN * 2;
  const candidates = data?.candidates || [];

  let y = PAGE_MARGIN;

  // Title
  doc.setFont("helvetica", "bold");
  doc.setFontSize(18);
  doc.setTextColor(15, 23, 42);
  y = writeWrapped(doc, "Candidate Comparison", PAGE_MARGIN, y, contentWidth, 22);

  doc.setFont("helvetica", "normal");
  doc.setFontSize(10);
  doc.setTextColor(100, 116, 139);
  const subtitleParts = [];
  if (data?.positionName) subtitleParts.push(data.positionName);
  subtitleParts.push(`${candidates.length} candidates`);
  if (data?.generatedAt) subtitleParts.push(new Date(data.generatedAt).toLocaleString());
  y = writeWrapped(doc, subtitleParts.join(" · "), PAGE_MARGIN, y, contentWidth, 14);
  doc.setTextColor(15, 23, 42);
  y += SECTION_GAP;

  // Candidate summary row
  y = writeSectionHeading(doc, "Candidates", PAGE_MARGIN, y);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(10);
  for (const c of candidates) {
    y = ensureSpace(doc, y, 18);
    const line = `${c.fullName || `User ${c.userId}`}  ·  Overall ${fmt(c.overallScore)}  ·  Video ${fmt(c.avgVideoScore)}  ·  Audio ${fmt(c.audioScore)}  ·  Integrity ${fmt(c.integrityScore)}`;
    doc.text(line, PAGE_MARGIN, y);
    y += 14;
  }
  y += SECTION_GAP;

  // Skill heatmap (0–100 scale)
  const skillNames = Array.from(
    new Set(
      candidates.flatMap((c) => (c.competencies || []).map((k) => k.skillName).filter(Boolean))
    )
  ).sort();

  if (skillNames.length > 0) {
    y = writeSectionHeading(doc, "Skill Heatmap", PAGE_MARGIN, y);
    const lookup = new Map();
    candidates.forEach((c) => {
      const inner = new Map();
      (c.competencies || []).forEach((k) => { if (k?.skillName) inner.set(k.skillName, k.score); });
      lookup.set(c.userId, inner);
    });
    const rows = skillNames.map((s) => ({
      label: s,
      scoreByUser: (c) => lookup.get(c.userId)?.get(s),
    }));
    y = drawHeatmap(doc, candidates, rows, y, { scale: "skill", labelCol: 200, secondaryCol: 0 });
    y += SECTION_GAP;
  }

  // Per-question scores (0–10 scale)
  const questionScores = data?.questionScores || [];
  if (questionScores.length > 0) {
    y = writeSectionHeading(doc, "Question-level Breakdown", PAGE_MARGIN, y);
    const rows = questionScores.map((q) => ({
      label: q.questionText || `#${q.questionId}`,
      secondary: q.competency,
      scoreByUser: q.scoreByUser || {},
    }));
    y = drawHeatmap(doc, candidates, rows, y, { scale: "question", labelCol: 260, secondaryCol: 90 });
    y += SECTION_GAP;
  }

  // AI Recommendation
  if (data?.aiRecommendation) {
    y = writeSectionHeading(doc, "AI Recommendation", PAGE_MARGIN, y);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(10);
    y = writeWrapped(doc, data.aiRecommendation, PAGE_MARGIN, y, contentWidth, 13);
    y += 4;
    if (data.suggestedOrdering) {
      doc.setFont("helvetica", "bold");
      doc.setFontSize(10);
      y = writeWrapped(doc, `Suggested order: ${data.suggestedOrdering}`, PAGE_MARGIN, y, contentWidth, 13);
    }
    if (data.flaggedCandidates && data.flaggedCandidates.length > 0) {
      doc.setTextColor(185, 28, 28);
      y = writeWrapped(doc, `Flagged: ${data.flaggedCandidates.join(", ")}`, PAGE_MARGIN, y, contentWidth, 13);
      doc.setTextColor(15, 23, 42);
    }
    y += SECTION_GAP;
  }

  // Per-candidate AI summary
  const summaries = candidates.filter((c) => c.aiSummary);
  if (summaries.length > 0) {
    y = writeSectionHeading(doc, "Per-Candidate Summaries", PAGE_MARGIN, y);
    for (const c of summaries) {
      y = ensureSpace(doc, y, 22);
      doc.setFont("helvetica", "bold");
      doc.setFontSize(11);
      y = writeWrapped(doc, c.fullName || `User ${c.userId}`, PAGE_MARGIN, y, contentWidth, 14);
      doc.setFont("helvetica", "normal");
      doc.setFontSize(10);
      y = writeWrapped(doc, c.aiSummary, PAGE_MARGIN, y, contentWidth, 13);
      y += 6;
    }
  }

  const blob = doc.output("blob");
  const name = `${slug(data?.positionName)}-comparison.pdf`;
  return { blob, name };
};

export const downloadComparePdf = (data) => {
  const { blob, name } = compareToPdfBlob(data);
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = name;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 1500);
};
